import { ArrowRight, Check } from 'lucide-react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { articleBySlug } from '@/modules/marketing/articles';
import { usePageMeta } from '@/lib/seo';
import { SiteFooter } from '@/modules/marketing/SiteFooter';
import { SiteHeader } from '@/modules/marketing/SiteHeader';

/**
 * One feature, one page.
 *
 * A single route renders every article from its slug, so adding a feature is adding an entry to the
 * articles file rather than writing another page. What differs between them is content, and content
 * belongs in data.
 *
 * ## Why this mirrors the landing page so closely
 *
 * Same `max-w-6xl` container, same centred `max-w-3xl` column inside it, same gradient behind the
 * hero, same footer. An earlier version set the body full-width and left-aligned, which read as a
 * different site wearing the same logo. A visitor who arrives here from a search result and then
 * clicks through to the landing page should not feel the seam.
 *
 * An unknown slug redirects home rather than rendering an empty shell — a marketing URL that
 * returns a blank page is worse than one that returns somewhere useful.
 */

/**
 * Renders a leading `**bold**` run as emphasis.
 *
 * Use-case and best-practice paragraphs read as a list where the first few words name the case and
 * the rest explains it. Marking that lead-in keeps the source readable as prose instead of forcing
 * every such paragraph into a separate `{ term, definition }` shape in the data.
 */
function Prose({ text }: { text: string }) {
  const match = /^\*\*(.+?)\*\*(.*)$/s.exec(text);
  if (match === null) {
    return <>{text}</>;
  }
  return (
    <>
      <strong className="text-foreground font-medium">{match[1]}</strong>
      {match[2]}
    </>
  );
}

export function ArticlePage() {
  const { slug } = useParams();
  const article = articleBySlug(slug);

  // Before the early return below, so the hook is never called conditionally. An unknown slug
  // redirects, but it must not be indexed on the way past either.
  usePageMeta({
    // The search title where the article has one, so the tab and the pre-rendered head agree.
    title: article?.seoTitle ?? article?.title ?? 'Article not found',
    description:
      article?.metaDescription ?? article?.standfirst ?? 'That article could not be found.',
    noindex: article === undefined,
  });

  if (article === undefined) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="bg-background min-h-screen">
      <SiteHeader />

      {/* The hero is the landing page's hero, structurally: gradient wash, centred column, eyebrow,
          headline, one paragraph, one button. */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="from-primary/10 pointer-events-none absolute inset-x-0 top-0 h-[32rem] bg-gradient-to-b to-transparent"
        />
        <div className="relative mx-auto max-w-6xl px-6 pt-16 pb-12 sm:pt-24">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-muted-foreground/80 text-xs font-medium tracking-[0.2em] uppercase">
              {article.eyebrow}
            </p>
            <h1 className="mt-6 text-3xl font-semibold tracking-tight text-balance sm:text-5xl">
              {article.title}
            </h1>
            <p className="text-muted-foreground mx-auto mt-5 max-w-xl text-base sm:text-lg">
              {article.standfirst}
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link
                to="/register"
                className="bg-primary text-primary-foreground rounded-md px-6 py-3 font-medium transition-opacity hover:opacity-90"
              >
                Start free trial
              </Link>
            </div>
          </div>

          <div className="mx-auto mt-14 max-w-3xl">
            <article.art priority />
          </div>
        </div>
      </section>

      <article className="mx-auto max-w-6xl px-6 pb-24">
        {article.sections.map((section) => (
          <section key={section.heading} className="mx-auto mt-20 max-w-3xl">
            {/* Heading centred like every section heading on the landing page; the paragraphs
                beneath stay left-aligned, because centred body copy makes the eye hunt for the
                start of each line. */}
            {/* An id per heading, so a menu or another page can link to a section rather than
                to the top of a long article and a scroll. Derived from the heading, which means
                renaming one breaks any link that pointed at it -- the Applications menu in
                SiteHeader is the caller to check. */}
            <h2
              id={section.heading
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-|-$/g, '')}
              className="scroll-mt-24 text-center text-xl font-semibold tracking-tight text-balance sm:text-2xl"
            >
              {section.heading}
            </h2>
            {section.paragraphs.map((paragraph) => (
              <p key={paragraph.slice(0, 40)} className="text-muted-foreground mt-4 leading-relaxed">
                <Prose text={paragraph} />
              </p>
            ))}
            {section.art !== undefined && (
              <div className="mt-8">
                <section.art />
              </div>
            )}
          </section>
        ))}

        {article.faq !== undefined && (
          <section className="mx-auto mt-24 max-w-3xl">
            <h2 className="text-center text-xl font-semibold tracking-tight sm:text-2xl">
              Frequently asked questions
            </h2>
            <dl className="mt-8 space-y-6">
              {article.faq.map((entry) => (
                <div
                  key={entry.q}
                  className="border-border/60 border-t pt-5 first:border-t-0 first:pt-0"
                >
                  <dt className="font-medium">{entry.q}</dt>
                  <dd className="text-muted-foreground mt-2 leading-relaxed">{entry.a}</dd>
                </div>
              ))}
            </dl>
          </section>
        )}

        <div className="border-border/60 bg-muted/10 mx-auto mt-20 max-w-3xl rounded-xl border p-6">
          <h2 className="text-sm font-medium">Key points</h2>
          <ul className="mt-4 space-y-2.5">
            {article.points.map((point) => (
              <li key={point} className="flex items-start gap-2.5 text-sm">
                <Check className="mt-0.5 size-4 shrink-0 text-emerald-500" aria-hidden />
                <span className="text-muted-foreground">{point}</span>
              </li>
            ))}
          </ul>
        </div>

        {article.related !== undefined && article.related.length > 0 && (
          <section className="mx-auto mt-20 max-w-3xl">
            <h2 className="text-center text-xl font-semibold tracking-tight sm:text-2xl">
              Related features
            </h2>
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {article.related.map((entry) => (
                <Link
                  key={entry.to}
                  to={entry.to}
                  className="border-border/60 hover:border-primary/40 hover:bg-muted/20 group flex items-center justify-between gap-4 rounded-xl border px-5 py-4 transition-colors"
                >
                  <span className="text-sm font-medium">{entry.label}</span>
                  <ArrowRight
                    className="text-primary size-4 shrink-0 transition-transform group-hover:translate-x-0.5"
                    aria-hidden
                  />
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* One closing call to action: a single button, and the objection it answers underneath. */}
        <div className="mt-20 text-center">
          <Link
            to="/register"
            className="bg-primary text-primary-foreground inline-flex items-center rounded-full px-8 py-3.5 font-medium transition-opacity hover:opacity-90"
          >
            Start free trial
          </Link>
        </div>
      </article>

      <SiteFooter />
    </div>
  );
}
